import React from 'react';
import styled from 'styled-components';
import { PaymentStats } from '../models/PaymentModels';
import { formatNumber, formatCurrency } from '../utils/numberFormatter';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from '../styles/designTokens';

// 통계 카드 컨테이너 - 단일 행 레이아웃
const StatsContainer = styled.div`
  display: flex;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[5]};
  overflow-x: auto;
  padding-bottom: ${spacing[2]};

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.secondary[100]};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary[300]};
    border-radius: 3px;

    &:hover {
      background: ${colors.secondary[400]};
    }
  }

  @media (max-width: 768px) {
    gap: ${spacing[3]};
    padding-bottom: 6px;
  }
`;

// 통계 카드 스타일 - 단일 행 최적화
const StatsCard = styled.div<{ $isMain?: boolean }>`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]} ${spacing[5]};
  box-shadow: ${shadows.sm};
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  flex: ${props => (props.$isMain ? '2' : '1')};
  min-width: ${props => (props.$isMain ? '300px' : '200px')};
  max-width: ${props => (props.$isMain ? 'none' : '280px')};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
    border-color: ${colors.neutral[300]};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${colors.primary[500]} 0%,
      ${colors.primary[700]} 100%
    );
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    min-width: ${props => (props.$isMain ? '200px' : '160px')};
    max-width: ${props => (props.$isMain ? 'none' : '220px')};
    padding: 14px ${spacing[4]};
  }
`;

// 카드 헤더 - 제목과 아이콘을 한 줄에
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[2]};
`;

// 카드 제목 스타일 - 더 작고 간결하게
const CardTitle = styled.h3`
  font-size: 13px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.tertiary};
  margin: 0;
  line-height: ${typography.lineHeight.snug};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// 카드 아이콘
const CardIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: ${borderRadius.md};
  background: linear-gradient(
    135deg,
    ${colors.primary[500]} 0%,
    ${colors.primary[700]} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.semantic.text.inverse};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
`;

// 메인 값 스타일 - 더 작고 효율적으로
const MainValue = styled.div`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  margin-bottom: 6px;
  line-height: ${typography.lineHeight.tight};
  font-family: ${typography.fontFamily.mono};
`;

// 변화량 표시 스타일 - 인라인으로 더 컴팩트하게
const ChangeIndicator = styled.div<{ isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props =>
    props.isPositive ? colors.success[600] : colors.error[600]};

  .icon {
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.bold};
  }
`;

// 로딩 스켈레톤 스타일 - 단일 행 최적화
const SkeletonCard = styled.div<{ $isMain?: boolean }>`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]} ${spacing[5]};
  box-shadow: ${shadows.sm};
  position: relative;
  overflow: hidden;
  flex: ${props => (props.$isMain ? '2' : '1')};
  min-width: ${props => (props.$isMain ? '300px' : '200px')};
  max-width: ${props => (props.$isMain ? 'none' : '280px')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${colors.neutral[200]} 0%,
      ${colors.neutral[300]} 100%
    );
  }

  .skeleton-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${spacing[2]};
  }

  .skeleton-title {
    height: 14px;
    width: 60%;
    background: linear-gradient(
      90deg,
      ${colors.neutral[100]} 25%,
      ${colors.neutral[200]} 50%,
      ${colors.neutral[100]} 75%
    );
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 3px;
  }

  .skeleton-icon {
    height: 24px;
    width: 24px;
    background: linear-gradient(
      90deg,
      ${colors.neutral[100]} 25%,
      ${colors.neutral[200]} 50%,
      ${colors.neutral[100]} 75%
    );
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: ${borderRadius.md};
  }

  .skeleton-value {
    height: 28px;
    background: linear-gradient(
      90deg,
      ${colors.neutral[100]} 25%,
      ${colors.neutral[200]} 50%,
      ${colors.neutral[100]} 75%
    );
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: ${borderRadius.base};
    margin-bottom: 6px;
  }

  .skeleton-change {
    height: 12px;
    width: 40%;
    background: linear-gradient(
      90deg,
      ${colors.neutral[100]} 25%,
      ${colors.neutral[200]} 50%,
      ${colors.neutral[100]} 75%
    );
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 3px;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @media (max-width: 768px) {
    min-width: ${props => (props.$isMain ? '200px' : '160px')};
    max-width: ${props => (props.$isMain ? 'none' : '220px')};
    padding: 14px ${spacing[4]};
  }
`;

// 에러 카드 스타일 - 단일 행 최적화
const ErrorCard = styled.div<{ $isMain?: boolean }>`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]} ${spacing[5]};
  text-align: center;
  position: relative;
  overflow: hidden;
  flex: ${props => (props.$isMain ? '2' : '1')};
  min-width: ${props => (props.$isMain ? '300px' : '200px')};
  max-width: ${props => (props.$isMain ? 'none' : '280px')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${colors.error[300]} 0%,
      ${colors.error[500]} 100%
    );
  }

  .icon {
    font-size: ${typography.fontSize.xl};
    margin-bottom: ${spacing[2]};
    opacity: 0.8;
  }

  .message {
    font-size: ${typography.fontSize.xs};
    color: ${colors.error[800]};
    font-weight: ${typography.fontWeight.medium};
  }

  @media (max-width: 768px) {
    min-width: ${props => (props.$isMain ? '200px' : '160px')};
    max-width: ${props => (props.$isMain ? 'none' : '220px')};
    padding: 14px ${spacing[4]};
  }
`;

// Props 인터페이스 (ViewModel에서 데이터 전달받음 - API 중복 호출 방지)
interface PaymentStatsCardsProps {
  stats: PaymentStats | null;
  loading: boolean;
  error: string | null;
  yearMonth: string;
}

// 결제 통계 카드 컴포넌트
const PaymentStatsCards: React.FC<PaymentStatsCardsProps> = ({
  stats,
  loading,
  error,
  yearMonth
}) => {
  // 로딩 상태
  if (loading) {
    return (
      <StatsContainer>
        <SkeletonCard $isMain>
          <div className="skeleton-header">
            <div className="skeleton-title" />
            <div className="skeleton-icon" />
          </div>
          <div className="skeleton-value" />
          <div className="skeleton-change" />
        </SkeletonCard>
        <SkeletonCard>
          <div className="skeleton-header">
            <div className="skeleton-title" />
            <div className="skeleton-icon" />
          </div>
          <div className="skeleton-value" />
          <div className="skeleton-change" />
        </SkeletonCard>
        <SkeletonCard>
          <div className="skeleton-header">
            <div className="skeleton-title" />
            <div className="skeleton-icon" />
          </div>
          <div className="skeleton-value" />
          <div className="skeleton-change" />
        </SkeletonCard>
      </StatsContainer>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <StatsContainer>
        <ErrorCard $isMain>
          <div className="icon">⚠️</div>
          <div className="message">통계 로드 실패</div>
        </ErrorCard>
        <ErrorCard>
          <div className="icon">⚠️</div>
          <div className="message">통계 로드 실패</div>
        </ErrorCard>
        <ErrorCard>
          <div className="icon">⚠️</div>
          <div className="message">통계 로드 실패</div>
        </ErrorCard>
      </StatsContainer>
    );
  }

  // 데이터가 없는 경우
  if (!stats) {
    return null;
  }

  return (
    <StatsContainer>
      {/* 결제 금액 카드 */}
      <StatsCard $isMain>
        <CardHeader>
          <CardTitle>{yearMonth.split('-')[1]}월 결제 금액</CardTitle>
          <CardIcon>₩</CardIcon>
        </CardHeader>
        <MainValue>{formatCurrency(stats.월결제금액)}</MainValue>
        <ChangeIndicator isPositive={(stats.증감금액 || 0) >= 0}>
          <span className="icon">{(stats.증감금액 || 0) >= 0 ? '↗' : '↘'}</span>
          <span>{(stats.증감금액 || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(stats.증감금액 || 0))}</span>
        </ChangeIndicator>
      </StatsCard>

      {/* 결제 건 카드 */}
      <StatsCard>
        <CardHeader>
          <CardTitle>결제 건</CardTitle>
          <CardIcon>#</CardIcon>
        </CardHeader>
        <MainValue>{formatNumber(stats.월결제건수)} 건</MainValue>
        <ChangeIndicator isPositive={(stats.증감건수 || 0) >= 0}>
          <span className="icon">{(stats.증감건수 || 0) >= 0 ? '↗' : '↘'}</span>
          <span>{(stats.증감건수 || 0) >= 0 ? '+' : ''}{(stats.증감건수 || 0)}건</span>
        </ChangeIndicator>
      </StatsCard>

      {/* 결제 고객 수 카드 */}
      <StatsCard>
        <CardHeader>
          <CardTitle>{yearMonth.split('-')[1]}월 결제 고객 수</CardTitle>
          <CardIcon>U</CardIcon>
        </CardHeader>
        <MainValue>{formatNumber(stats.월결제고객수)} 명</MainValue>
        <ChangeIndicator isPositive={(stats.증감고객수 || 0) >= 0}>
          <span className="icon">{(stats.증감고객수 || 0) >= 0 ? '↗' : '↘'}</span>
          <span>{(stats.증감고객수 || 0) >= 0 ? '+' : ''}{(stats.증감고객수 || 0)}명</span>
        </ChangeIndicator>
      </StatsCard>
    </StatsContainer>
  );
};

export default PaymentStatsCards; 